import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useBetaSignup } from '../context/BetaSignupContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { TrendingUp, Search, Globe, Wallet, Lock as LockIcon, Compass, Zap, Sparkles, Lock } from 'lucide-react';
import { investmentStrategies } from '../mockData';

const Offerings = () => {
  const location = useLocation();
  const { openModal } = useBetaSignup();

  const iconMap = {
    TrendingUp: TrendingUp,
    Search: Search,
    Globe: Globe,
    Wallet: Wallet,
    Lock: LockIcon,
    Compass: Compass,
    Zap: Zap,
    Sparkles: Sparkles,
    Shield: TrendingUp
  };

  // Handle anchor scrolling when navigating from Home page
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Small delay to ensure content is rendered
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#F5F3ED]">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-6xl md:text-7xl font-light mb-6 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
            Investment Strategies<br />
            Built for Growth
          </h1>
          <p className="text-xl text-gray-600 font-light max-w-3xl mx-auto">
            We've built proprietary investment products across asset classes,
            each designed to help you reach your financial goals.
          </p>
        </div>
      </section>

      {/* Investment Strategies */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="space-y-16">
            {investmentStrategies.map((strategy, index) => {
              const Icon = iconMap[strategy.icon];
              return (
                <Card 
                  key={strategy.id} 
                  id={strategy.id}
                  className="overflow-hidden border-0 shadow-lg scroll-mt-24"
                  data-testid={`offering-card-${strategy.id}`}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="p-8 lg:p-12">
                      <div className="inline-block px-3 py-1 bg-[#FF8C42] text-white text-xs font-medium rounded-full mb-4">
                        {strategy.category}
                      </div>
                      <h2 className="text-4xl font-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>{strategy.name}</h2>
                      <p className="text-gray-600 font-light mb-6 leading-relaxed">{strategy.description}</p>
                      
                      <div className="space-y-4 mb-8">
                        <div className="flex justify-between items-center border-b pb-3">
                          <span className="text-sm text-gray-600 font-light">Target Return</span>
                          <span className="text-lg font-light text-green-600">{strategy.targetReturn}</span>
                        </div>
                        <div className="flex justify-between items-center border-b pb-3">
                          <span className="text-sm text-gray-600 font-light">Liquidity</span>
                          <span className="text-lg font-light">{strategy.liquidity}</span>
                        </div>
                        <div className="flex justify-between items-center border-b pb-3">
                          <span className="text-sm text-gray-600 font-light">Minimum Investment</span>
                          <span className="text-lg font-light">${strategy.minInvestment}</span>
                        </div>
                        <div className="flex justify-between items-center border-b pb-3">
                          <span className="text-sm text-gray-600 font-light">Management Fee</span>
                          <span className="text-lg font-light">{strategy.fee}%</span>
                        </div>
                        <div className="flex justify-between items-center border-b pb-3">
                          <span className="text-sm text-gray-600 font-light">Success Fee</span>
                          <span className="text-lg font-light">{strategy.successFee}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 font-light">Risk Level</span>
                          <span className="text-lg font-light">{strategy.riskLevel}</span>
                        </div>
                      </div>

                      <div className="mb-8">
                        <h3 className="font-light text-lg mb-3">Asset Allocation</h3>
                        <div className="space-y-2">
                          {strategy.allocation.map((alloc, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                              <span className="text-sm text-gray-700 font-light">{alloc.category}</span>
                              <div className="flex items-center gap-3">
                                <div className="w-32 bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-[#FF8C42] h-2 rounded-full"
                                    style={{ width: `${alloc.percentage}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm font-light w-10">{alloc.percentage}%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* View Full Portfolio button - disabled, triggers beta modal */}
                      <Button 
                        size="lg" 
                        variant="outline"
                        onClick={openModal}
                        className="w-full lg:w-auto rounded-full px-8 font-light border-2 border-gray-300 text-gray-500 hover:bg-gray-100 hover:text-gray-700 cursor-pointer"
                        data-testid={`view-portfolio-btn-${strategy.id}`}
                      >
                        <Lock className="mr-2" size={16} />
                        View Full Portfolio
                      </Button>
                    </div>

                    <div className="bg-white p-8 lg:p-12 flex flex-col items-center justify-center border-l border-gray-200">
                      <div className="w-32 h-32 mb-6 flex items-center justify-center">
                        <Icon className="w-32 h-32" strokeWidth={1.5} style={{ color: '#FF8C42' }} />
                      </div>
                      <div className="text-center">
                        <p className="text-4xl font-light mb-2" style={{ fontFamily: 'Georgia, serif' }}>{strategy.targetReturn}</p>
                        <p className="text-sm text-gray-600 font-light">Target Return</p>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#1a1a3e] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Ready to start investing?
          </h2>
          <p className="text-xl text-gray-400 mb-8 font-light">
            Join thousands of investors building wealth with Trove.
          </p>
          <Button 
            size="lg" 
            className="bg-[#FF8C42] text-white hover:bg-[#FFB366] px-10 py-7 rounded-full font-light"
            onClick={openModal}
            data-testid="offerings-cta-btn"
          >
            BECOME A CLIENT
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Offerings;
